/**
 * Supabase Draft Service
 * Hybrid architecture: PostgreSQL for metadata + Supabase Storage for large mesh files
 *
 * This service handles:
 * - Saving draft metadata to PostgreSQL (lightweight: ~30KB)
 * - Uploading mesh files to Supabase Storage (large: 10-30MB)
 * - Loading drafts with mesh data
 * - Listing drafts by orderId
 * - Deleting drafts and associated mesh files
 */

import { supabase, DRAFT_MESHES_BUCKET, isSupabaseConfigured } from '../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';

class SupabaseDraftService {
  constructor() {
    this.bucketName = DRAFT_MESHES_BUCKET;
    this.tableName = 'drafts';
  }

  /**
   * Check if Supabase is configured and available
   */
  isAvailable() {
    return isSupabaseConfigured();
  }

  /**
   * Generate unique draft ID
   */
  generateDraftId() {
    return uuidv4();
  }

  /**
   * Get storage path for a mesh file
   * Format: {orderId}/{draftId}/filename.json
   */
  getStoragePath(orderId, draftId, filename) {
    // Use 'temp' as orderId until backend provides real orderIds
    const safeOrderId = orderId || 'temp';
    return `${safeOrderId}/${draftId}/${filename}`;
  }

  /**
   * Upload mesh data to Supabase Storage
   * @param {string} path - Storage path
   * @param {string} content - JSON string content
   * @returns {Promise<{success: boolean, path?: string, error?: string}>}
   */
  async uploadMeshFile(path, content) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Uploading mesh file: ${path}`);
      console.log(`[SupabaseDraftService] Content size: ${(content.length / 1024 / 1024).toFixed(2)}MB`);

      // Convert string to Blob for upload
      const blob = new Blob([content], { type: 'application/json' });

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .upload(path, blob, {
          contentType: 'application/json',
          upsert: true, // Overwrite if exists
        });

      if (error) {
        throw new Error(`Storage upload failed: ${error.message}`);
      }

      console.log(`[SupabaseDraftService] Upload successful: ${path}`);

      return {
        success: true,
        path: data.path,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] Upload error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Download mesh data from Supabase Storage
   * @param {string} path - Storage path
   * @returns {Promise<{success: boolean, content?: string, error?: string}>}
   */
  async downloadMeshFile(path) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Downloading mesh file: ${path}`);

      // Download from Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .download(path);

      if (error) {
        throw new Error(`Storage download failed: ${error.message}`);
      }

      // Convert Blob to text
      const content = await data.text();

      console.log(`[SupabaseDraftService] Download successful: ${path}`);
      console.log(`[SupabaseDraftService] Content size: ${(content.length / 1024 / 1024).toFixed(2)}MB`);

      return {
        success: true,
        content,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] Download error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Delete mesh file from Supabase Storage
   * @param {string} path - Storage path
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteMeshFile(path) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Deleting mesh file: ${path}`);

      const { error } = await supabase.storage
        .from(this.bucketName)
        .remove([path]);

      if (error) {
        // Don't throw error if file doesn't exist
        if (error.message.includes('not found')) {
          console.warn(`[SupabaseDraftService] File not found (already deleted?): ${path}`);
          return { success: true };
        }
        throw new Error(`Storage delete failed: ${error.message}`);
      }

      console.log(`[SupabaseDraftService] Delete successful: ${path}`);

      return { success: true };
    } catch (error) {
      console.error('[SupabaseDraftService] Delete error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Save draft data (metadata to PostgreSQL, meshes to Storage)
   * @param {string} draftName - Human-readable draft name
   * @param {object} draftData - Complete draft data object
   * @param {string} [orderId] - Order ID (optional, uses 'temp' if not provided)
   * @returns {Promise<{success: boolean, draftId?: string, error?: string}>}
   */
  async saveDraft(draftName, draftData, orderId = 'temp') {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
      }

      const draftId = this.generateDraftId();
      console.log(`[SupabaseDraftService] Saving draft: ${draftId}`);

      // 1. Upload mesh files to Storage
      const shadedMeshPath = this.getStoragePath(orderId, draftId, 'shaded_mesh.json');
      let unmodifiedMeshPath = null;

      // Upload shaded mesh (required)
      if (!draftData.meshContent?.shadedMesh) {
        throw new Error('Shaded mesh content is required');
      }

      const shadedUpload = await this.uploadMeshFile(shadedMeshPath, draftData.meshContent.shadedMesh);
      if (!shadedUpload.success) {
        throw new Error(`Failed to upload shaded mesh: ${shadedUpload.error}`);
      }

      // Upload unmodified mesh (optional)
      if (draftData.meshContent?.unmodifiedMesh) {
        unmodifiedMeshPath = this.getStoragePath(orderId, draftId, 'unmodified_mesh.json');
        const unmodifiedUpload = await this.uploadMeshFile(unmodifiedMeshPath, draftData.meshContent.unmodifiedMesh);
        if (!unmodifiedUpload.success) {
          console.warn('Failed to upload unmodified mesh:', unmodifiedUpload.error);
          // Continue anyway - unmodified mesh is optional
        }
      }

      // 2. Save metadata to PostgreSQL
      const draftRecord = {
        id: draftId,
        order_id: orderId,
        draft_name: draftName,
        workflow_step: draftData.workflowStep || 'UNKNOWN',
        metadata: draftData.metadata || {},
        tool_states: draftData.toolStates || {},
        materials: draftData.materials || {},
        shaded_mesh_path: shadedMeshPath,
        unmodified_mesh_path: unmodifiedMeshPath,
      };

      const { error: dbError } = await supabase
        .from(this.tableName)
        .insert(draftRecord);

      if (dbError) {
        // If DB insert fails, clean up uploaded files
        await this.deleteMeshFile(shadedMeshPath);
        if (unmodifiedMeshPath) {
          await this.deleteMeshFile(unmodifiedMeshPath);
        }
        throw new Error(`Database insert failed: ${dbError.message}`);
      }

      console.log(`[SupabaseDraftService] Draft saved successfully: ${draftId}`);

      return {
        success: true,
        draftId,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] Save error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Load draft data (metadata from PostgreSQL, meshes from Storage)
   * @param {string} draftId - Draft ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async loadDraft(draftId) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Loading draft: ${draftId}`);

      // 1. Fetch metadata from PostgreSQL
      const { data: draftRecord, error: dbError } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', draftId)
        .is('deleted_at', null)
        .single();

      if (dbError) {
        throw new Error(`Database query failed: ${dbError.message}`);
      }

      if (!draftRecord) {
        throw new Error('Draft not found');
      }

      // 2. Download mesh files from Storage
      const shadedMeshResult = await this.downloadMeshFile(draftRecord.shaded_mesh_path);
      if (!shadedMeshResult.success) {
        throw new Error(`Failed to download shaded mesh: ${shadedMeshResult.error}`);
      }

      let unmodifiedMeshContent = null;
      if (draftRecord.unmodified_mesh_path) {
        const unmodifiedMeshResult = await this.downloadMeshFile(draftRecord.unmodified_mesh_path);
        if (unmodifiedMeshResult.success) {
          unmodifiedMeshContent = unmodifiedMeshResult.content;
        } else {
          console.warn('Failed to download unmodified mesh:', unmodifiedMeshResult.error);
          // Continue anyway - unmodified mesh is optional
        }
      }

      // 3. Reconstruct draft data structure
      const draftData = {
        metadata: {
          ...draftRecord.metadata,
          draftId: draftRecord.id,
          draftName: draftRecord.draft_name,
          orderId: draftRecord.order_id,
          timestamp: draftRecord.created_at,
        },
        workflowStep: draftRecord.workflow_step,
        toolStates: draftRecord.tool_states,
        materials: draftRecord.materials,
        meshData: {
          shadedMesh: draftRecord.shaded_mesh_path,
          unmodifiedMesh: draftRecord.unmodified_mesh_path,
        },
        meshContent: {
          shadedMesh: shadedMeshResult.content,
          unmodifiedMesh: unmodifiedMeshContent,
        },
      };

      console.log(`[SupabaseDraftService] Draft loaded successfully: ${draftId}`);

      return {
        success: true,
        data: draftData,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] Load error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * List all drafts for an order (or all drafts if no orderId)
   * @param {string} [orderId] - Optional order ID filter
   * @param {object} [options] - Query options (limit, offset, orderBy)
   * @returns {Promise<{success: boolean, drafts?: array, error?: string}>}
   */
  async listDrafts(orderId = null, options = {}) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Listing drafts${orderId ? ` for order: ${orderId}` : ''}`);

      // Build query
      let query = supabase
        .from(this.tableName)
        .select('id, order_id, draft_name, workflow_step, metadata, created_at, updated_at')
        .is('deleted_at', null);

      // Filter by orderId if provided
      if (orderId) {
        query = query.eq('order_id', orderId);
      }

      // Apply ordering
      const orderBy = options.orderBy || 'created_at';
      const orderDirection = options.orderDirection || 'desc';
      query = query.order(orderBy, { ascending: orderDirection === 'asc' });

      // Apply pagination
      if (options.limit) {
        query = query.limit(options.limit);
      }
      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      // Execute query
      const { data: records, error: dbError } = await query;

      if (dbError) {
        throw new Error(`Database query failed: ${dbError.message}`);
      }

      // Format results to match existing interface
      const drafts = records.map(record => ({
        id: record.id,
        name: record.draft_name,
        timestamp: record.created_at,
        workflowStep: record.workflow_step,
        orderId: record.order_id,
      }));

      console.log(`[SupabaseDraftService] Found ${drafts.length} drafts`);

      return {
        success: true,
        drafts,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] List error:', error);
      return {
        success: false,
        error: error.message,
        drafts: [],
      };
    }
  }

  /**
   * Delete a draft and its associated mesh files
   * @param {string} draftId - Draft ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteDraft(draftId) {
    try {
      if (!this.isAvailable()) {
        throw new Error('Supabase is not configured');
      }

      console.log(`[SupabaseDraftService] Deleting draft: ${draftId}`);

      // 1. Get draft record to find mesh paths
      const { data: draftRecord, error: fetchError } = await supabase
        .from(this.tableName)
        .select('shaded_mesh_path, unmodified_mesh_path')
        .eq('id', draftId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch draft: ${fetchError.message}`);
      }

      if (!draftRecord) {
        throw new Error('Draft not found');
      }

      // 2. Delete mesh files from Storage
      const filesToDelete = [draftRecord.shaded_mesh_path];
      if (draftRecord.unmodified_mesh_path) {
        filesToDelete.push(draftRecord.unmodified_mesh_path);
      }

      for (const path of filesToDelete) {
        const deleteResult = await this.deleteMeshFile(path);
        if (!deleteResult.success) {
          console.warn(`Failed to delete mesh file: ${path}`, deleteResult.error);
          // Continue anyway - we still want to delete the DB record
        }
      }

      // 3. Soft delete the draft record (or hard delete if preferred)
      // Using soft delete for safety - can be recovered if needed
      const { error: deleteError } = await supabase
        .from(this.tableName)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', draftId);

      // For hard delete instead:
      // const { error: deleteError } = await supabase
      //   .from(this.tableName)
      //   .delete()
      //   .eq('id', draftId);

      if (deleteError) {
        throw new Error(`Database delete failed: ${deleteError.message}`);
      }

      console.log(`[SupabaseDraftService] Draft deleted successfully: ${draftId}`);

      return {
        success: true,
        draftId,
      };
    } catch (error) {
      console.error('[SupabaseDraftService] Delete error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get public URL for a mesh file (for direct downloads if needed)
   * @param {string} path - Storage path
   * @returns {string} Public URL
   */
  getPublicUrl(path) {
    if (!this.isAvailable()) {
      return null;
    }

    const { data } = supabase.storage
      .from(this.bucketName)
      .getPublicUrl(path);

    return data.publicUrl;
  }
}

// Export singleton instance
export const supabaseDraftService = new SupabaseDraftService();
export default supabaseDraftService;
